<template>
	<App title="State Management">
		<div class="w-100 position-relative">
			<Block class="border-top border-secondary border-2 col-md-10 mx-auto">
				<template #header>
					<h3 class="block-title">State Management</h3>
				</template>

				<!-- State Management Form -->
				<div class="row">
					<div class="col-12">
						<div class="card border-0">
							<div class="card-header bg-light border-0">
								<h5 class="card-title mb-0">
									<i class="bi bi-map text-secondary me-2"></i>
									Create New State
								</h5>
							</div>
							<div class="card-body p-4">
								<Form ref="stateForm" @submit="onStateSubmit">
									<div class="row g-3">
										<!-- Country Selection -->
										<div class="col-12 col-md-6">
											<label for="stateCountry" class="form-label text-dark fw-normal">
												<i class="bi bi-flag me-1"></i>Country
											</label>
											<SelectField 
												id="stateCountry" 
												v-model="stateData.country" 
												name="country" 
												rules="required" 
												label="Country" 
												:options="country_options"
												@change="onCountryChange"
											>
											</SelectField>
										</div>

										<!-- Region Selection -->
										<div class="col-12 col-md-6">
											<label for="stateRegion" class="form-label text-dark fw-normal">
												<i class="bi bi-geo-alt me-1"></i>Region
											</label>
											<SelectField 
												id="stateRegion" 
												v-model="stateData.region" 
												name="region" 
												rules="required" 
												label="Region" 
												:options="region_options"
												:disabled="!stateData.country"
											>
											</SelectField>
										</div>

										<!-- State Name -->
										<div class="col-12 col-md-6">
											<label for="stateName" class="form-label text-dark fw-normal">
												<i class="bi bi-map me-1"></i>State Name
											</label>
											<InputField 
												id="stateName" 
												v-model="stateData.state" 
												name="state" 
												rules="required|min:3|max:50" 
												label="State Name" 
												placeholder="Enter state name..." 
											/>
										</div>

										<!-- State Manager -->
										<div class="col-12 col-md-6">
											<label for="stateManager" class="form-label text-dark fw-normal">
												<i class="bi bi-person-badge me-1"></i>Manager (Optional)
											</label>
											<SelectField 
												id="stateManager" 
												v-model="stateData.manager" 
												name="manager" 
												label="Manager" 
												:options="manager_options"
											>
											</SelectField>
										</div>

										<!-- Form Buttons -->
										<div class="col-12">
											<div class="d-grid gap-2 d-md-flex justify-content-md-end pt-3">
												<button 
													type="button" 
													class="btn bg-danger text-white me-2 px-4" 
													:disabled="stateLoading || stateDone" 
													@click="clearStateForm"
												>
													<i class="bi bi-x-circle me-1"></i>Clear
												</button>
												<Button 
													id="stateAction" 
													type="submit" 
													:label="stateLabels" 
													:loading="stateLoading" 
													:done="stateDone" 
													class="btn bg-secondary text-light px-4" 
													:disabled="checkStateInput" 
												/>
											</div>
										</div>
									</div>
								</Form>
							</div>
						</div>
					</div>
				</div>
			</Block>

			<!-- Notification Area -->
			<div class="mt-9 position-absolute top-0 end-0">
				<NotivueKeyboard v-slot="{ containersTabIndex }">
					<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
						<AppNotification :item="item" />
					</Notivue>
				</NotivueKeyboard>
			</div>
		</div>
	</App>
</template>

<script lang="ts">
import $ from 'jquery'
import { useConfirm } from '@/libs/useConfirm.ts'
import locations from '@/json/location.json'
import Swal from 'sweetalert2'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import { Component, Ref, Vue, toNative } from 'vue-facing-decorator'
import { Notivue, push, materialTheme, NotivueKeyboard } from 'notivue'
import AppNotification from '@/vue/components/AppNotification.vue'

@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		Button,
		Block,
		SelectField,
		TextareaField,
		Notivue,
		AppNotification,
		NotivueKeyboard
	},
})
class StateManagement extends Vue {
	confirm = useConfirm().confirm
	materialTheme = materialTheme

	// State Form Data
	stateLoading = false
	stateDone = false
	stateData = {
		country: '',
		region: '',
		state: '',
		manager: '',
	}

	@Ref('stateForm')
	readonly stateForm!: InstanceType<typeof Form>

	// Computed Properties
	get country_options(): Select2Options {
		return {
			placeholder: 'Select Country',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('country.ajax.select.country'),
			},
			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
		}
	}

	get region_options(): Select2Options {
		return {
			placeholder: 'Select Region',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('region.ajax.select.region'),
				data: (params: any) => ({
					...params,
					country: this.stateData.country
				}),
			},
			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
		}
	}

	get manager_options(): Select2Options {
		return {
			placeholder: 'Select Manager',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('admin.ajax.select.admin'),
			},
			//@ts-ignore
			templateResult: ({ id, fullname, email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, fullname, email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
		}
	}

	get stateLabels(): ButtonLabel {
		return {
			normal: 'Create State',
			loading: 'Creating...',
			done: 'Created'
		}
	}

	get checkStateInput(): boolean {
		return [this.stateData.country, this.stateData.region, this.stateData.state].some(field => field.trim() === '')
	}

	// Methods
	clearStateForm() {
		this.stateData = {
			country: '',
			region: '',
			state: '',
			manager: '',
		}
		this.stateForm.reset()
	}

	onCountryChange() {
		// Clear region when country changes
		this.stateData.region = ''
	}

	// Form Submission Methods
	async onStateSubmit(payload: any) {
		this.stateLoading = true

		this.$http
			.post(this.route('state.page.create'), payload)
			.then((response) => {
				Swal.fire({
					title: 'Success!',
					text: 'State created successfully!',
					icon: 'success',
					confirmButtonText: 'OK'
				}).then(() => {
					this.clearStateForm()
					this.stateDone = false
					setTimeout(()=>{
						this.router.visit(this.route('state.page.group'))
					},3000)
				
				})	
			})
			.catch((e) => {
				push.error({
					title: 'Error',
					message: 'Unable to create state at this time. Please try again later.'
				})
				
			})
			.finally(() => (this.stateLoading = false))
	}
}

export default toNative(StateManagement)
</script>